import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-hot-toast";
import { useUpdatePayoutPartner } from "../hooks/usePayoutPartners";

const PayoutPartnerEditForm = ({ isOpen, onClose, record }) => {
  const [form, setForm] = useState(null);
  const { mutate: updatePayoutPartner, isPending } = useUpdatePayoutPartner();

  React.useEffect(() => {
    if (record) {
      setForm({
        priority: record.priority ?? 1,
        isActive: record.isActive ?? true,
        payoutFeeType: record.payoutFeeType ?? "flat",
        payoutFeeValue: record.payoutFeeValue ?? 0,
        payoutFeeCap: record.payoutFeeCap ?? 0,
        platformFeeValue: record.platformFeeValue ?? 0,
        minPayoutPlatformFee: record.minPayoutPlatformFee ?? "",
        minPayoutValue: record.minPayoutValue ?? 10,
        maxPayoutValue: record.maxPayoutValue ?? 2000000,
      });
    }
  }, [record]);

  if (!isOpen || !record || !form) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    updatePayoutPartner(
      {
        uuid: record.uuid,
        payload: {
          priority: Number(form.priority),
          isActive: form.isActive,
          payoutFeeType: form.payoutFeeType,
          payoutFeeValue: Number(form.payoutFeeValue),
          payoutFeeCap: Number(form.payoutFeeCap),
          platformFeeValue: Number(form.platformFeeValue),
          minPayoutPlatformFee:
            form.minPayoutPlatformFee === "" ? null : Number(form.minPayoutPlatformFee),
          minPayoutValue: Number(form.minPayoutValue),
          maxPayoutValue: Number(form.maxPayoutValue),
        },
      },
      {
        onSuccess: () => {
          toast.success(`${record.businessPayoutsProvider?.name || "Provider"} priority updated`);
          onClose();
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to update payout partner");
        },
      }
    );
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div className="overflow-y-auto mx-4 w-full max-w-lg max-h-[90vh] bg-white rounded-2xl shadow-2xl">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Edit {record.currencyCode?.toUpperCase()} · {record.businessPayoutsProvider?.name}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
            <div>
              <span className="block text-xs font-bold tracking-wider uppercase">Country</span>
              <span className="text-gray-800 uppercase">{record.countryCode}</span>
            </div>
            <div>
              <span className="block text-xs font-bold tracking-wider uppercase">Payout Method</span>
              <span className="text-gray-800">{record.businessPayoutMethod?.name || "—"}</span>
            </div>
          </div>

          <div>
            <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Priority (lower number tried first)
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={form.priority}
              onChange={(e) => handleChange("priority", e.target.value)}
              className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <label className="flex gap-2 items-center text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => handleChange("isActive", e.target.checked)}
              className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
            />
            Active
          </label>

          <div>
            <span className="block mb-3 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Payout Amount Limits
            </span>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-500">
                  Minimum Amount
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.minPayoutValue}
                  onChange={(e) => handleChange("minPayoutValue", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-500">
                  Maximum Amount
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.maxPayoutValue}
                  onChange={(e) => handleChange("maxPayoutValue", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-100">
            <span className="block mb-3 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Payout Fee
            </span>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-500">
                  Fee Type
                </label>
                <select
                  value={form.payoutFeeType}
                  onChange={(e) => handleChange("payoutFeeType", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="flat">Flat</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-xs font-medium text-gray-500">
                  Fee Value {form.payoutFeeType === "percentage" ? "(%)" : ""}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.payoutFeeValue}
                  onChange={(e) => handleChange("payoutFeeValue", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {form.payoutFeeType === "percentage" && (
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-500">
                    Fee Cap (0 = uncapped)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.payoutFeeCap}
                    onChange={(e) => handleChange("payoutFeeCap", e.target.value)}
                    className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              )}

              <div>
                <label className="block mb-1 text-xs font-medium text-gray-500">
                  Platform Fee
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.platformFeeValue}
                  onChange={(e) => handleChange("platformFeeValue", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block mb-1 text-xs font-medium text-gray-500">
                  Minimum Platform Fee (blank = currency default)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Use currency default"
                  value={form.minPayoutPlatformFee}
                  onChange={(e) => handleChange("minPayoutPlatformFee", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end px-6 py-4 bg-gray-50 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {isPending ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayoutPartnerEditForm;
